import Layout from "@components/layout/layout";
import { captureException } from "@sentry/react";
import { ERROR_MSG } from "@utils/enum";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { trackBulkSearch } from "../analytics";
import { BULK_LOGS_PATH, FROM_BULK_LOGS, MAX_CONSECUTIVE_POLL_ERRORS, RESULTS_POLL_INTERVAL_MS, RESULTS_POLL_TIMEOUT_MS } from "../constants";
import { confirmBulkMatch, getApiErrorMessage, getBulkMatchResults } from "../services";
import { applyResultFilters, buildMatchSummary, countActiveFilters, DEFAULT_RESULT_FILTERS, getUnreviewedRows } from "../utils";
import ConfirmationRequiredModal from "./ConfirmationRequiredModal";
import ConfirmMappingBanner from "./ConfirmMappingBanner";
import MatchResultsSkeleton from "./MatchResultsSkeleton";
import MatchResultsTable from "./MatchResultsTable";
import MatchSummaryPopover from "./MatchSummaryPopover";
import ResultsFilters from "./ResultsFilters";
import type { BulkJobStatus, BulkMatchRow, BulkNavigationState, ConfirmStrategy, MatchCandidate, ResultFilters, ResultStatusFilter } from "../types";

const SUMMARY_SHORTCUTS: Record<Exclude<ResultStatusFilter, "ALL">, ResultFilters> = {
  AUTO_MATCHED: { confidence: ["HIGH_CONFIDENCE"], review: [] },
  NEEDS_REVIEW: { confidence: [], review: ["NEEDS_REVIEW"] },
  NO_MATCH: { confidence: ["NO_MATCH"], review: [] },
};

const BulkSearchResults = () => {
  const history = useHistory();
  const { searchId } = useParams<{ searchId: string }>();
  const location = useLocation<BulkNavigationState | undefined>();
  const cameFromLogs = location.state?.from === FROM_BULK_LOGS;

  const [rows, setRows] = useState<BulkMatchRow[]>([]);
  const [jobStatus, setJobStatus] = useState<BulkJobStatus>("PROCESSING");
  const [totalRecords, setTotalRecords] = useState(location.state?.totalRecords ?? 0);
  const [filters, setFilters] = useState<ResultFilters>(DEFAULT_RESULT_FILTERS);
  const [showSummary, setShowSummary] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [pollNonce, setPollNonce] = useState(0);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollErrorsRef = useRef(0);
  const lastPollErrorRef = useRef<unknown>(null);
  const reviewOverridesRef = useRef<Record<string, MatchCandidate>>({});

  const summary = useMemo(() => buildMatchSummary(rows), [rows]);
  const visibleRows = useMemo(() => applyResultFilters(rows, filters), [rows, filters]);
  const unreviewedRows = useMemo(() => getUnreviewedRows(rows), [rows]);
  const activeFilterCount = countActiveFilters(filters);
  const isProcessing = jobStatus === "PROCESSING";
  const showMatchControls = !isProcessing;

  const activeSummaryShortcut = useMemo<ResultStatusFilter>(() => {
    if (!filters.review.length && filters.confidence.length === 1) {
      if (filters.confidence[0] === "HIGH_CONFIDENCE") return "AUTO_MATCHED";
      if (filters.confidence[0] === "NO_MATCH") return "NO_MATCH";
    }
    if (!filters.confidence.length && filters.review.length === 1 && filters.review[0] === "NEEDS_REVIEW") return "NEEDS_REVIEW";
    return "ALL";
  }, [filters]);

  // Returns the job status, or null when the request itself failed. A failed
  // request says nothing about the job: matching runs in a Celery worker and
  // carries on regardless, so the caller retries instead of stopping.
  const fetchResults = useCallback(async (): Promise<BulkJobStatus | null> => {
    try {
      const data = await getBulkMatchResults(searchId);
      pollErrorsRef.current = 0;
      // Already confirmed: there is nothing left to review, so this page would
      // only offer to confirm it again. Go to the final page instead, keeping
      // the navigation state so its Go back still knows where we came from.
      if (data.is_confirmed) {
        history.replace({ pathname: `/company-search/bulk/${searchId}/companies`, state: location.state });
        return "COMPLETED";
      }
      setRows(
        (data.rows ?? []).map((row) => {
          const normalised = { ...row, candidates: (row.candidates ?? []).filter(Boolean) };
          const chosen = reviewOverridesRef.current[row.row_id];
          if (!chosen) return normalised;
          return { ...normalised, matched_company: { company_id: chosen.company_id, name: chosen.name, country: chosen.country, country_code: chosen.country_code }, confidence: chosen.confidence, is_reviewed: true };
        })
      );
      setTotalRecords(data.total_records ?? 0);
      setJobStatus(data.status);
      if (data.status === "FAILED") toast.error("We couldn't complete matching for this file.");
      return data.status;
    } catch (error) {
      pollErrorsRef.current += 1;
      lastPollErrorRef.current = error;
      captureException(error);
      return null;
    }
  }, [searchId, history, location.state]);

  useEffect(() => {
    let isActive = true;
    const startedAt = Date.now();
    pollErrorsRef.current = 0;
    setHasTimedOut(false);

    const poll = async () => {
      const status = await fetchResults();
      if (!isActive) return;

      // Stop only on a terminal status reported by the server.
      if (status === "COMPLETED" || status === "FAILED") return;

      // status === null means the request failed. Keep polling; give up only
      // after several consecutive failures, and surface it as "still running"
      // rather than as a failed job, because the worker is unaffected.
      if (status === null && pollErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
        toast.error(getApiErrorMessage(lastPollErrorRef.current, ERROR_MSG));
        setHasTimedOut(true);
        return;
      }

      if (Date.now() - startedAt > RESULTS_POLL_TIMEOUT_MS) {
        setHasTimedOut(true);
        return;
      }
      pollTimerRef.current = setTimeout(poll, RESULTS_POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      isActive = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchResults, pollNonce]);

  const handleCandidateSelected = (rowId: string, candidate: MatchCandidate) => {
    reviewOverridesRef.current = { ...reviewOverridesRef.current, [rowId]: candidate };
    setRows((previous) =>
      previous.map((row) =>
        row.row_id === rowId
          ? {
              ...row,
              matched_company: { company_id: candidate.company_id, name: candidate.name, country: candidate.country, country_code: candidate.country_code },
              confidence: candidate.confidence,
              is_reviewed: true,
            }
          : row
      )
    );
  };

  const applyConfirmation = async (strategy: ConfirmStrategy) => {
    try {
      const overrides = rows.filter((row) => row.is_reviewed && row.matched_company).map((row) => ({ row_id: row.row_id, company_id: row.matched_company?.company_id ?? "" }));
      await confirmBulkMatch(searchId, { strategy, overrides });
      trackBulkSearch("companyBulkSearchConfirmed", { search_id: searchId, strategy, reviewed_count: overrides.length, unreviewed_count: unreviewedRows.length, total_records: totalRecords });

      setShowConfirmModal(false);
      toast.success("Mapping confirmed. Trade information is now available.");
      history.replace({ pathname: `/company-search/bulk/${searchId}/companies`, state: location.state });
    } catch (error) {
      captureException(error);
      toast.error(getApiErrorMessage(error, ERROR_MSG));
    }
  };

  const handleConfirmClick = () => {
    if (unreviewedRows.length > 0) {
      setShowConfirmModal(true);
      return;
    }
    applyConfirmation("TOP_MATCH");
  };

  return (
    <Layout type="companySearch" pageTitle="Bulk Company Search | Trademo Intel" setNotification={() => null}>
      <div className="tw-text-gray-900">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-5 tw-py-4">
          <button type="button" onClick={() => history.push(cameFromLogs ? BULK_LOGS_PATH : "/company-search")} className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-base tw-text-gray-max">
            <span className="material-symbols-outlined tw-font-semibold tw-text-2xl tw-leading-none">arrow_left_alt</span>
            Go back
          </button>

          <div className="tw-relative">
            {showMatchControls && (
              <span className="tw-text-base tw-text-gray-max">
                {totalRecords} companies uploaded.{" "}
                <button type="button" onClick={() => setShowSummary((open) => !open)} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-primary-purple-600 hover:tw-underline">
                  View match summary
                </button>
              </span>
            )}
            {showSummary && showMatchControls && (
              <MatchSummaryPopover
                summary={summary}
                activeFilter={activeSummaryShortcut}
                onFilterChange={(next) => {
                  setFilters(next === "ALL" ? DEFAULT_RESULT_FILTERS : SUMMARY_SHORTCUTS[next]);
                  setShowSummary(false);
                }}
                onClose={() => setShowSummary(false)}
              />
            )}
          </div>

          {showMatchControls && (
            <div className="tw-relative tw-flex tw-items-center tw-gap-2">
              {activeFilterCount > 0 && (
                <button type="button" onClick={() => setFilters(DEFAULT_RESULT_FILTERS)} className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-600">
                  Clear filters
                  <span className="material-symbols-outlined tw-text-base tw-leading-none">close</span>
                </button>
              )}
              <button type="button" onClick={() => setShowFilters((open) => !open)} className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium hover:tw-bg-gray-50 ${activeFilterCount ? "tw-border-primary-purple-600 tw-bg-primary-purple-50 tw-text-primary-purple-600" : "tw-border-gray-300 tw-bg-white tw-text-gray-900"}`}>
                <span className="material-symbols-outlined tw-text-xl tw-leading-none">filter_list</span>
                Filters
                {activeFilterCount > 0 && <span className="tw-rounded-full tw-bg-primary-purple-600 tw-px-1.5 tw-text-xs tw-text-white">{activeFilterCount}</span>}
              </button>
              {showFilters && <ResultsFilters filters={filters} onChange={setFilters} onClose={() => setShowFilters(false)} />}
            </div>
          )}
        </div>

        {isProcessing ? (
          hasTimedOut ? (
            <div className="tw-py-24 tw-text-center tw-text-sm tw-text-gray-600">
              <div>This is taking longer than expected. Matching is still running in the background.</div>
              <button type="button" onClick={() => setPollNonce((nonce) => nonce + 1)} className="tw-mt-3 tw-h-9 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
                Check again
              </button>
            </div>
          ) : (
            <>
              <div className="tw-pb-3 tw-text-center tw-text-sm tw-text-gray-600">Matching your companies… this may take a few minutes.</div>
              {visibleRows.length > 0 ? <MatchResultsTable rows={visibleRows} onCandidateSelected={handleCandidateSelected} isProcessing /> : <MatchResultsSkeleton rowCount={totalRecords} />}
            </>
          )
        ) : (
          <>
            <div className="tw-px-5 tw-pb-3">
              <ConfirmMappingBanner onConfirm={handleConfirmClick} />
            </div>
            <MatchResultsTable rows={visibleRows} onCandidateSelected={handleCandidateSelected} />
          </>
        )}
      </div>

      <ConfirmationRequiredModal show={showConfirmModal} reviewCount={unreviewedRows.length} confirmedCount={summary.auto_matched} onCancel={() => setShowConfirmModal(false)} onConfirm={applyConfirmation} />
    </Layout>
  );
};

export default BulkSearchResults;
